import type { TrackerData } from "@finn-no/pulse-sdk";
import type { Section } from "./landingpageContent.js";

/**
 * Typed views over the opaque `Section` payloads served by the mb-new-car-api
 * `mobility-frontpage-sections` endpoint (Blocket car "Nya bilar" tab). The wire
 * type stays `Section { type: string; payload: unknown }`; these narrow it for
 * web rendering. The app-API still forwards payloads verbatim and never reads them.
 */

export const NEW_CAR_MAKE_LIST = "newCarMakeList";
export const NEW_CAR_FEATURED_MODELS = "newCarFeaturedModels";
export const NEW_CAR_LATEST_MODELS = "newCarLatestModels";
export const NEW_CAR_CAMPAIGNS = "newCarCampaigns";

/**
 * Public URL prefix the mb-new-car-frontend app is served under (its `BASE_PATH`).
 * Make page: `${NEW_CAR_BASE_PATH}/{make}`; model page: `${NEW_CAR_BASE_PATH}/{make}/{model}`.
 */
export const NEW_CAR_BASE_PATH = "/mobility/new/car";

export interface NewCarMakeListItem {
    id: string;
    name: string;
    image: string;
    logoUrl: string;
    modelCount: number;
    /** Link to the make's showroom page; computed server-side in renderPage. */
    href: string;
    /** Pulse "Go to Brand Page" click event (MBNC_ev07), built server-side. */
    trackingEvent?: TrackerData;
}

export interface NewCarMakeListPayload {
    header: string;
    description?: string | null;
    items: NewCarMakeListItem[];
}

export interface HighlightedSpecification {
    label: string;
    value: string;
}

export interface NewCarFeaturedModelItem {
    id: string;
    /**
     * Make slug — the make segment of the model-page link `/{makeId}/{id}` (supplied by mb-new-car-api).
     */
    makeId: string;
    name: string;
    displayPrice: string;
    image: string;
    highlightedSpecifications: HighlightedSpecification[];
    /** Link to the model page (`/{makeId}/{id}`); computed server-side in renderPage. */
    href: string;
    /** Pulse "Go to Model Page" click event (MBNC_ev05), built server-side. */
    trackingEvent?: TrackerData;
}

export interface NewCarFeaturedModelsPayload {
    header: string;
    items: NewCarFeaturedModelItem[];
}

export interface NewCarLatestModelItem {
    id: string;
    /**
     * Make slug — the make segment of the model-page link `/{makeId}/{id}` (supplied by mb-new-car-api).
     */
    makeId: string;
    name: string;
    displayPrice: string;
    image: string;
    /** Card panel colour (hex). */
    backgroundColor: string;
    /** Text treatment on the panel. */
    textColor: "light" | "dark";
    /** Link to the model page (`/{makeId}/{id}`); computed server-side in renderPage. */
    href: string;
    /** Pulse "Go to Model Page" click event (MBNC_ev05), built server-side. */
    trackingEvent?: TrackerData;
}

export interface NewCarLatestModelsPayload {
    header: string;
    /** Call-to-action button label. */
    ctaButton: string;
    items: NewCarLatestModelItem[];
}

export interface NewCarCampaignItem {
    id: string;
    /** Make slug — the make segment of the model-page link `/{makeId}/{id}`. */
    makeId: string;
    name: string;
    /** Not tracked: there is one card per model, so the model id identifies it. */
    campaignId: string;
    /** The API may add types, so these stay open strings. */
    campaignTypes: string[];
    /** Label and Warp icon name, both chosen by the API. */
    badge: { label: string; icon: string };
    /** The API guarantees it never advertises rates or loans. */
    title: string;
    imageUrl: string;
    /** Link to the model page (`/{makeId}/{id}`); computed server-side in renderPage. */
    href: string;
    /** Pulse "Click new car campaign card" click event (MBNC_ev30), built server-side. */
    trackingEvent?: TrackerData;
}

export interface NewCarCampaignsPayload {
    header: string;
    /** "Se alla kampanjer" — the label of the link to the campaign overview page. */
    seeAllLabel: string;
    /** Call-to-action button label. */
    ctaButton: string;
    items: NewCarCampaignItem[];
}

/**
 * Typed view of the mb-new-car-api `MobilityFrontpageSection` (its `oneOf` payload), keyed on the
 * sibling `type` (the API declares no discriminator mapping, so `type` is the tag). Hand-mirrored
 * from the swagger contract (`/api-docs`). The generic wire `Section` in landingpageContent.ts stays
 * `payload: unknown` for the app-api passthrough — this is the typed view for web rendering.
 * All four section types are rendered on the web "Nya bilar" tab (see `renderNewCarSection`).
 */
export type NewCarSection =
    | { type: typeof NEW_CAR_MAKE_LIST; payload: NewCarMakeListPayload }
    | {
          type: typeof NEW_CAR_FEATURED_MODELS;
          payload: NewCarFeaturedModelsPayload;
      }
    | { type: typeof NEW_CAR_LATEST_MODELS; payload: NewCarLatestModelsPayload }
    | { type: typeof NEW_CAR_CAMPAIGNS; payload: NewCarCampaignsPayload };

export interface NewCarSectionsResponse {
    sections: NewCarSection[];
}

// The concrete union members, so the predicates' return types stay in sync with NewCarSection.
type NewCarMakeListSection = Extract<NewCarSection, { type: typeof NEW_CAR_MAKE_LIST }>;
type NewCarFeaturedModelsSection = Extract<NewCarSection, { type: typeof NEW_CAR_FEATURED_MODELS }>;
type NewCarLatestModelsSection = Extract<NewCarSection, { type: typeof NEW_CAR_LATEST_MODELS }>;
type NewCarCampaignsSection = Extract<NewCarSection, { type: typeof NEW_CAR_CAMPAIGNS }>;

export const isMakeListSection = (section: Section): section is NewCarMakeListSection =>
    section.type === NEW_CAR_MAKE_LIST && Array.isArray((section.payload as Partial<NewCarMakeListPayload> | null)?.items);

export const isFeaturedModelsSection = (section: Section): section is NewCarFeaturedModelsSection =>
    section.type === NEW_CAR_FEATURED_MODELS && Array.isArray((section.payload as Partial<NewCarFeaturedModelsPayload> | null)?.items);

export const isNewCarLatestModelsSection = (section: Section): section is NewCarLatestModelsSection =>
    section.type === NEW_CAR_LATEST_MODELS && Array.isArray((section.payload as Partial<NewCarLatestModelsPayload> | null)?.items);

export const isNewCarCampaignsSection = (section: Section): section is NewCarCampaignsSection =>
    section.type === NEW_CAR_CAMPAIGNS && Array.isArray((section.payload as Partial<NewCarCampaignsPayload> | null)?.items);

/**
 * The make-list payload, but only when it actually carries brands. Returns `undefined`
 * when the section is missing, malformed, or its `items` are empty. This is the single signal
 * that gates "Nya bilar" tab visibility (see `newCarSectionsForVisibleTab`).
 */
export const findPopulatedMakeList = (sections: Section[]): NewCarMakeListPayload | undefined => {
    const section = sections.find(isMakeListSection);
    return section && section.payload.items.length > 0 ? section.payload : undefined;
};

/**
 * Build a model page link (`/{makeId}/{id}`) from the API-supplied make slug and model slug.
 * `makeId` is always present on the wire (mb-new-car-api ships it for every model card).
 */
export const modelPageHref = (makeId: string, modelId: string): string => `${NEW_CAR_BASE_PATH}/${makeId}/${modelId}`;
